import useSEO from "../hooks/useSEO";
import { Link } from "react-router-dom";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { NEWS } from "../data/content";

export default function News() {
  useSEO({
    title: "News",
    description: "Latest news, press and updates from Ideal Entertainment.",
  });

  const featured = NEWS.find((n) => n.slug);
  const rest = NEWS.filter((n) => n !== featured);

  return (
    <>
      <PageHero
        overline="Latest News"
        title="Press & updates"
        subtitle="Headlines, releases and stories from across our productions."
        image="https://images.unsplash.com/photo-1619099619226-f96e319e3732?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
      />

      {featured && (
        <section className="pt-24 md:pt-32">
          <div className="container-x">
            <Reveal>
              <Link
                to={`/news/${featured.slug}`}
                data-testid="news-featured"
                className="group grid grid-cols-1 gap-8 overflow-hidden rounded-3xl border border-white/5 bg-[#0a0a0a] transition-all hover:border-gold/30 lg:grid-cols-2"
              >
                <div className="relative aspect-video overflow-hidden lg:aspect-auto lg:h-full">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-col justify-center p-8 lg:p-12">
                  <span className="overline text-gold">Featured · {featured.date}</span>
                  <h2 className="mt-4 font-playfair text-2xl leading-tight text-white sm:text-3xl lg:text-4xl">
                    {featured.title}
                  </h2>
                  <p className="mt-5 text-sm font-light leading-relaxed text-neutral-400 sm:text-base">
                    {featured.excerpt}
                  </p>
                  <span className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-white transition-colors group-hover:text-gold">
                    Read the full story <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          </div>
        </section>
      )}

      <section className="section-y">
        <div className="container-x grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((n, i) => {
            const inner = (
              <>
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={n.image}
                    alt={n.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <span className="text-xs uppercase tracking-[0.2em] text-gold">{n.date}</span>
                  <h3 className="mt-3 font-playfair text-lg leading-snug text-white">{n.title}</h3>
                  <p className="mt-3 flex-1 text-sm font-light leading-relaxed text-neutral-400">
                    {n.excerpt}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm text-neutral-300 transition-colors group-hover:text-gold">
                    Read more <ArrowUpRight size={16} />
                  </span>
                </div>
              </>
            );
            const cls =
              "group flex h-full flex-col overflow-hidden rounded-2xl border border-white/5 bg-[#0a0a0a] transition-all hover:border-gold/30";
            return (
              <Reveal key={i} delay={i * 0.06}>
                {n.slug ? (
                  <Link to={`/news/${n.slug}`} data-testid={`news-card-${i}`} className={cls}>
                    {inner}
                  </Link>
                ) : (
                  <a href={n.url} target="_blank" rel="noreferrer" data-testid={`news-card-${i}`} className={cls}>
                    {inner}
                  </a>
                )}
              </Reveal>
            );
          })}
        </div>
      </section>
    </>
  );
}
